/* =========================================================================
 * WCAG 2.2 AA navigation enhancements (WS4.4 / F-2.1.2-a; applies under both 2.1 AA and 2.2 AA)
 *
 * What this file adds on top of the theme's existing nav behavior:
 *  - Syncs `aria-expanded` on the hamburger trigger
 *    (`.js-drawer-open-left-link`) when the mobile drawer opens / closes.
 *  - Restores keyboard focus to the hamburger trigger after the drawer
 *    closes, so a keyboard user's position is preserved (required by
 *    APG Disclosure / Menu-Button patterns).
 *  - Syncs `aria-expanded` on the cart trigger
 *    (`.js-drawer-open-right-link`) when the cart drawer opens / closes,
 *    and restores focus to the last-used cart trigger on close.
 *  - Focus trap on the mobile nav drawer
 *    (F-2.4.3-a, post-pilot client report 2026-05-06 Issue #2,
 *    2026-05-07 Issue #2, and 2026-05-07 Issue #2 follow-up):
 *    moves focus into `#NavDrawer` on open (close button preferred) and
 *    cycles Tab / Shift+Tab among the drawer's currently-focusable
 *    descendants while it's open. The focusable list is recomputed each
 *    Tab so the accordion sub-menus expanding / collapsing don't trap
 *    focus on hidden elements. APG Dialog (Modal) pattern. Tab is always
 *    preventDefault-ed inside the trap and focus is moved programmatically;
 *    the browser's natural Tab order cannot be trusted because the theme's
 *    `tabindexMobileNav()` strips tabindex from drawer contents at
 *    `window.innerWidth > 767`, leaving Tab to escape onto the page.
 *    Visibility filtering uses `Element.checkVisibility()` because the
 *    theme's collapsed accordions clip via `max-height: 0; overflow: hidden`
 *    on the parent `<ul class="mobile-nav__sublist">` while leaving the
 *    children's intrinsic `offsetWidth`/`offsetHeight` non-zero, so the
 *    older offset/clientRects check leaked every hidden submenu item into
 *    the cycle (e.g., Tab from the SHOP chevron stepped through ~50
 *    invisible submenu links before reaching LEARN).
 *
 * The theme already has an Escape-key handler wired at
 * `document.addEventListener("keyup", this.onKeyUp)` that calls
 * `closeDrawers()` -> `toggleLeftDrawer(false)` + `toggleRightDrawer("cart",
 * false)`. We do NOT re-add Escape handling. We only observe the resulting
 * body class change and sync ARIA + focus.
 *
 * Why MutationObserver and not event-based: the theme's drawer open/close
 * events have minified names in `theme.min.js`; the canonical, stable
 * signal is the body class list (`js-drawer-open-left`,
 * `js-drawer-open-right`). Observing the class list is resilient to future
 * minification changes.
 * ========================================================================= */
(function () {
  'use strict';

  if (window.__wcagNavEnhancementsInit) return;
  window.__wcagNavEnhancementsInit = true;

  var LEFT_CLASS = 'js-drawer-open-left';
  var RIGHT_CLASS = 'js-drawer-open-right';
  var LEFT_TRIGGER_SELECTOR = '.js-drawer-open-left-link';
  var RIGHT_TRIGGER_SELECTOR = '.js-drawer-open-right-link';
  var DRAWER_ID = 'NavDrawer';
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ].join(',');

  var lastRightTrigger = null;
  var trapState = { handler: null };

  function isVisible(el) {
    // Prefer Element.checkVisibility() — it correctly accounts for
    // `display: none`, `visibility: hidden` cascading from an ancestor,
    // and `content-visibility: hidden`. Crucially, it returns false for
    // elements clipped to zero height by an ancestor with
    // `max-height: 0; overflow: hidden` (the pattern this theme uses for
    // collapsed mobile-nav sub-lists), where `offsetWidth`/`offsetHeight`
    // would still report the element's intrinsic dimensions and falsely
    // mark it visible. Supported in Chrome 105+, Firefox 125+, Safari
    // 17.4+; fall back to a manual walk for older browsers.
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
    }
    if (el.offsetWidth === 0 && el.offsetHeight === 0 && el.getClientRects().length === 0) return false;
    for (var p = el; p && p.nodeType === 1; p = p.parentElement) {
      var s = window.getComputedStyle(p);
      if (s.display === 'none' || s.visibility === 'hidden' || s.visibility === 'collapse') return false;
    }
    return true;
  }

  function getFocusable(root) {
    var nodes = root.querySelectorAll(FOCUSABLE_SELECTOR);
    var visible = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (!isVisible(n)) continue;
      // Skip elements explicitly hidden from assistive tech.
      if (n.closest('[aria-hidden="true"]')) continue;
      visible.push(n);
    }
    return visible;
  }

  function installLeftDrawerTrap() {
    var drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;
    // Move focus into the drawer after the open transition has had a
    // chance to render. Prefer the close button; fall back to the first
    // visible focusable; last fall back is the dialog itself
    // (which carries `tabindex="-1"`).
    window.requestAnimationFrame(function () {
      var focusables = getFocusable(drawer);
      var closeBtn = drawer.querySelector('.mobile-menu--close-btn');
      var target = (closeBtn && focusables.indexOf(closeBtn) !== -1)
        ? closeBtn
        : (focusables[0] || drawer);
      try { target.focus(); } catch (e) { /* no-op */ }
    });

    // Defensive: if a previous open left a handler installed, drop it
    // before adding a new one so we never stack listeners.
    if (trapState.handler) {
      document.removeEventListener('keydown', trapState.handler, true);
    }
    trapState.handler = function (e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      // If the drawer has since closed, do nothing — uninstall on the
      // close transition will clean up shortly.
      if (!document.body.classList.contains(LEFT_CLASS)) return;
      // Recompute every Tab — sub-menu accordions change visibility live.
      var focusables = getFocusable(drawer);
      if (!focusables.length) {
        e.preventDefault();
        try { drawer.focus(); } catch (err) { /* no-op */ }
        return;
      }
      // Always preventDefault and advance focus programmatically. We can't
      // rely on the browser's natural Tab order: the theme's
      // `tabindexMobileNav()` sets tabindex="-1" on every <a> and <button>
      // inside #NavDrawer when window.innerWidth > 767, which strips the
      // drawer's contents out of the browser's tab cycle entirely. With
      // boundary-only preventDefault the browser would skip the drawer and
      // land on the next document-tabbable element (Avada accessibility
      // widget, then the hamburger trigger underneath the overlay).
      // Programmatic .focus() works on tabindex="-1" elements, so cycling
      // through `focusables` keeps the user inside the dialog regardless of
      // what the theme has done with tabindex.
      e.preventDefault();
      var active = document.activeElement;
      var idx = focusables.indexOf(active);
      var nextIdx;
      if (e.shiftKey) {
        nextIdx = idx <= 0 ? focusables.length - 1 : idx - 1;
      } else {
        nextIdx = (idx === -1 || idx === focusables.length - 1) ? 0 : idx + 1;
      }
      try { focusables[nextIdx].focus(); } catch (err) { /* no-op */ }
    };
    document.addEventListener('keydown', trapState.handler, true);
  }

  function uninstallLeftDrawerTrap() {
    if (trapState.handler) {
      document.removeEventListener('keydown', trapState.handler, true);
      trapState.handler = null;
    }
  }

  function setExpanded(selector, isOpen) {
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  }

  function restoreFocus(selector) {
    var trigger = document.querySelector(selector);
    if (trigger && typeof trigger.focus === 'function') {
      // Use rAF so focus lands after the drawer's own close transitions
      // have finished updating the DOM (avoids fighting other handlers).
      window.requestAnimationFrame(function () {
        try { trigger.focus(); } catch (e) { /* no-op */ }
      });
    }
  }

  // Track the most recently clicked cart trigger so we can focus-return to
  // the one the user actually used (header icon vs. mobile footer icon).
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var trigger = target.closest(RIGHT_TRIGGER_SELECTOR);
    if (trigger) lastRightTrigger = trigger;
  }, true);

  function syncFromBodyClasses(prevLeft, prevRight) {
    var cls = document.body.classList;
    var isLeftOpen = cls.contains(LEFT_CLASS);
    var isRightOpen = cls.contains(RIGHT_CLASS);

    if (isLeftOpen !== prevLeft) {
      setExpanded(LEFT_TRIGGER_SELECTOR, isLeftOpen);
      if (isLeftOpen) {
        installLeftDrawerTrap();
      } else {
        uninstallLeftDrawerTrap();
        restoreFocus(LEFT_TRIGGER_SELECTOR);
      }
    }
    if (isRightOpen !== prevRight) {
      setExpanded(RIGHT_TRIGGER_SELECTOR, isRightOpen);
      if (!isRightOpen) {
        if (lastRightTrigger && document.contains(lastRightTrigger)) {
          window.requestAnimationFrame(function () {
            try { lastRightTrigger.focus(); } catch (e) { /* no-op */ }
          });
        } else {
          restoreFocus(RIGHT_TRIGGER_SELECTOR);
        }
      }
    }
    return { left: isLeftOpen, right: isRightOpen };
  }

  function startObserver() {
    if (!document.body) return;
    var state = {
      left: document.body.classList.contains(LEFT_CLASS),
      right: document.body.classList.contains(RIGHT_CLASS)
    };
    // Seed aria-expanded to match current drawer state on load.
    setExpanded(LEFT_TRIGGER_SELECTOR, state.left);
    setExpanded(RIGHT_TRIGGER_SELECTOR, state.right);

    var observer = new MutationObserver(function () {
      var next = syncFromBodyClasses(state.left, state.right);
      state.left = next.left;
      state.right = next.right;
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserver, { once: true });
  } else {
    startObserver();
  }
})();
